<template>
  <div>
    <b-row class="mb-3">
      <b-col>
        <h5>Field Mappings</h5>
      </b-col>
      <b-col cols="auto">
        <b-button variant="primary" size="sm" @click="openCreateModal">
          <i class="fa fa-plus mr-1" />
          Add Mapping
        </b-button>
      </b-col>
    </b-row>

    <b-table
      :items="mappings"
      :fields="fields"
      :busy="loading"
      responsive
      show-empty
    >
      <template #cell(direction)="{ item }">
        <b-badge :variant="item.direction === 'inbound' ? 'info' : 'warning'">
          {{ item.direction }}
        </b-badge>
      </template>

      <template #cell(is_active)="{ item }">
        <b-badge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <template #cell(field_mappings)="{ item }">
        {{ (item.field_mappings || []).length }} fields
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditModal(item)">
          <i class="fa fa-edit" />
        </b-button>
        <b-button size="sm" variant="outline-danger" @click="confirmDelete(item)">
          <i class="fa fa-trash" />
        </b-button>
      </template>

      <template #table-busy>
        <div class="text-center my-3"><b-spinner variant="primary" /></div>
      </template>
    </b-table>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="editingMapping ? 'Edit Mapping' : 'Create Mapping'"
      size="lg"
      @ok="saveMapping"
      :ok-disabled="!mappingForm.ams_entity || !mappingForm.external_entity"
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Direction *">
            <b-form-select v-model="mappingForm.direction" :options="directionOptions" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Active">
            <b-form-checkbox v-model="mappingForm.is_active" switch />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="AMS Entity *">
            <b-form-select v-model="mappingForm.ams_entity" :options="amsEntityOptions">
              <template #first>
                <b-form-select-option :value="''">Select entity...</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="External Entity *">
            <b-form-input v-model="mappingForm.external_entity" placeholder="e.g. wc_product, wc_order" />
          </b-form-group>
        </b-col>
      </b-row>

      <h6 class="mt-3">Field Mappings</h6>
      <div v-for="(fm, idx) in mappingForm.field_mappings" :key="idx" class="mb-2">
        <b-row>
          <b-col md="4">
            <b-form-input v-model="fm.source_field" placeholder="Source field" size="sm" />
          </b-col>
          <b-col md="1" class="text-center pt-1">
            <i class="fa fa-arrow-right" />
          </b-col>
          <b-col md="4">
            <b-form-input v-model="fm.target_field" placeholder="Target field" size="sm" />
          </b-col>
          <b-col md="2">
            <b-form-select v-model="fm.transformation" :options="transformOptions" size="sm" />
          </b-col>
          <b-col md="1">
            <b-button size="sm" variant="outline-danger" @click="removeFieldMapping(idx)">
              <i class="fa fa-times" />
            </b-button>
          </b-col>
        </b-row>
      </div>
      <b-button size="sm" variant="outline-primary" @click="addFieldMapping">
        <i class="fa fa-plus mr-1" /> Add Field
      </b-button>
    </b-modal>

    <!-- Delete Confirm -->
    <b-modal v-model="showDeleteModal" title="Confirm Delete" @ok="deleteMapping">
      <p>Are you sure you want to delete this mapping?</p>
    </b-modal>
  </div>
</template>

<script>
export default {
  props: {
    connectorId: { type: Number, required: true },
  },
  data() {
    return {
      loading: false,
      showModal: false,
      showDeleteModal: false,
      editingMapping: null,
      selectedMapping: null,
      mappingForm: {
        direction: 'inbound',
        ams_entity: '',
        external_entity: '',
        is_active: true,
        field_mappings: [],
        transform_rules: null,
        sync_config: null,
      },
      fields: [
        { key: 'direction', label: 'Direction' },
        { key: 'ams_entity', label: 'AMS Entity' },
        { key: 'external_entity', label: 'External Entity' },
        { key: 'field_mappings', label: 'Fields' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
      amsEntityOptions: [
        { value: 'products', text: 'Products' },
        { value: 'assets', text: 'Assets' },
        { value: 'orders', text: 'Orders' },
        { value: 'customers', text: 'Customers' },
        { value: 'inventory', text: 'Inventory' },
        { value: 'users', text: 'Users' },
      ],
      directionOptions: [
        { value: 'inbound', text: 'Inbound (AMS → External)' },
        { value: 'outbound', text: 'Outbound (External → AMS)' },
      ],
      transformOptions: [
        { value: null, text: 'None' },
        { value: 'uppercase', text: 'Uppercase' },
        { value: 'lowercase', text: 'Lowercase' },
        { value: 'trim', text: 'Trim' },
        { value: 'to_integer', text: 'To Integer' },
        { value: 'to_string', text: 'To String' },
        { value: 'date_format', text: 'Date Format' },
      ],
    }
  },
  computed: {
    mappings() {
      return this.$store.state.appConnector.mappings
    },
  },
  async mounted() {
    this.loading = true
    try {
      await this.$store.dispatch('appConnector/fetchMappings', this.connectorId)
      if (!this.mappings || this.mappings.length === 0) {
        await this.$store.dispatch('appConnector/seedDefaultMappings', this.connectorId)
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    openCreateModal() {
      this.editingMapping = null
      this.mappingForm = {
        direction: 'inbound',
        ams_entity: '',
        external_entity: '',
        is_active: true,
        field_mappings: [{ source_field: '', target_field: '', transformation: null }],
        transform_rules: null,
        sync_config: null,
      }
      this.showModal = true
    },
    openEditModal(mapping) {
      this.editingMapping = mapping
      this.mappingForm = {
        direction: mapping.direction,
        ams_entity: mapping.ams_entity,
        external_entity: mapping.external_entity,
        is_active: mapping.is_active,
        field_mappings: [...(mapping.field_mappings || []).map(fm => ({ ...fm }))],
        transform_rules: mapping.transform_rules,
        sync_config: mapping.sync_config,
      }
      if (this.mappingForm.field_mappings.length === 0) {
        this.addFieldMapping()
      }
      this.showModal = true
    },
    addFieldMapping() {
      this.mappingForm.field_mappings.push({ source_field: '', target_field: '', transformation: null })
    },
    removeFieldMapping(idx) {
      this.mappingForm.field_mappings.splice(idx, 1)
    },
    async saveMapping() {
      try {
        const payload = {
          ...this.mappingForm,
          field_mappings: this.mappingForm.field_mappings.filter(fm => fm.source_field && fm.target_field),
        }

        if (this.editingMapping) {
          await this.$store.dispatch('appConnector/updateMapping', {
            connectorId: this.connectorId,
            mappingId: this.editingMapping.id,
            data: payload,
          })
        } else {
          await this.$store.dispatch('appConnector/createMapping', {
            connectorId: this.connectorId,
            data: payload,
          })
        }
        this.showModal = false
      } catch (err) {
        // handled in store
      }
    },
    confirmDelete(mapping) {
      this.selectedMapping = mapping
      this.showDeleteModal = true
    },
    async deleteMapping() {
      if (this.selectedMapping) {
        await this.$store.dispatch('appConnector/deleteMapping', {
          connectorId: this.connectorId,
          mappingId: this.selectedMapping.id,
        })
        this.showDeleteModal = false
        this.selectedMapping = null
      }
    },
  },
}
</script>
